import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Preloader } from 'atoms/loader/Preloader';
import { CardFront } from 'components/card/Front';
import { ClaimRewardsDialog } from 'components/rewards/ClaimRewardsDialog';
import { Discount } from 'components/rewards/Discount';
import { Reward } from 'components/rewards/types';
import { useReferralRewards } from 'hooks/referral/useReferralRewards';
import { fantasy, glossary } from 'lib/glossary';
import { monetaryAmountFragment } from 'lib/monetaryAmount';

import { ClaimReferralRewardsDialog_referralReward } from './__generated__/index.graphql';

type Props = {
  open: boolean;
  onClose: () => void;
  referralRewards: ClaimReferralRewardsDialog_referralReward[];
};

const HeaderWrapper = styled.p`
  padding-top: var(--triple-unit);
  color: var(--c-white);
  text-transform: uppercase;
  font-size: 32px;
  font-family:
    Druk Wide,
    sans-serif;
`;

const Header = () => {
  return (
    <HeaderWrapper>
      <FormattedMessage {...fantasy.referralReward} />
    </HeaderWrapper>
  );
};

const formatReward = (
  referralReward: ClaimReferralRewardsDialog_referralReward,
  teasers: ReactElement[],
  cardBack: ReactElement | null,
  claimed: boolean
): Reward | undefined => {
  if (!cardBack) {
    return undefined;
  }
  const { card, conversionCredit } = referralReward;

  return {
    ids: [referralReward.id],
    key: referralReward.id,
    backgroundText: conversionCredit ? (
      <FormattedMessage {...glossary.discount} />
    ) : (
      card?.rarityTyped || ''
    ),
    header: <Header />,
    back: cardBack,
    front: isClaimed => {
      return conversionCredit ? (
        <Discount
          readyToShow={!!isClaimed}
          percentage={conversionCredit.percentageDiscount}
          maxDiscount={conversionCredit.maxDiscount}
        />
      ) : (
        <CardFront src={card?.pictureUrl || ''} />
      );
    },
    teasers,
    claimed,
  };
};

export const ClaimReferralRewardsDialog = ({
  open,
  onClose,
  referralRewards,
}: Props) => {
  const { claim, isClaimed, makeCardBackAndTeaser } = useReferralRewards();

  if (referralRewards.length === 0) {
    return null;
  }

  const { formattedRewards, groupedRewards } = referralRewards.reduce<{
    formattedRewards: Reward[];
    groupedRewards: Record<string, ClaimReferralRewardsDialog_referralReward>;
  }>(
    (result, referralReward) => {
      const { cardback, teasers } = makeCardBackAndTeaser(referralReward);
      const formattedReward = formatReward(
        referralReward,
        teasers,
        cardback,
        isClaimed(referralReward)
      );
      if (formattedReward) {
        result.formattedRewards.push(formattedReward);
      }
      result.groupedRewards[referralReward.id] = referralReward;
      return result;
    },
    {
      formattedRewards: [],
      groupedRewards: {},
    }
  );

  return (
    <>
      <Preloader
        imageUrls={referralRewards
          .map(reward => reward.card?.pictureUrl)
          .filter(url => !!url)}
      />
      <ClaimRewardsDialog
        open={open}
        onClose={onClose}
        onClaim={ids => {
          ids.forEach(id => {
            const reward = groupedRewards[id];
            if (reward) {
              claim(groupedRewards[id]);
            }
          });
        }}
        rewards={formattedRewards}
      />
    </>
  );
};

ClaimReferralRewardsDialog.fragments = {
  referralReward: gql`
    fragment ClaimReferralRewardsDialog_referralReward on ReferralReward {
      id
      card: anyCard {
        slug
        pictureUrl
      }
      conversionCredit {
        id
        maxDiscount {
          ...MonetaryAmountFragment_monetaryAmount
        }
        percentageDiscount
      }
      ...useReferralRewards_referralReward
    }
    ${monetaryAmountFragment}
    ${useReferralRewards.fragments.referralReward}
  ` as TypedDocumentNode<ClaimReferralRewardsDialog_referralReward>,
};
